<template>
	<v-app>
		<v-app-bar
			:elevation="0"
			color="#FFFFFF"
			app
			absolute
			light
			:permanent="$vuetify.breakpoint.mdAndUp"
			height="100">
			<v-container id="nav-top" class="fill-height" fluid>
				<v-row class="align-md-center" no-gutters>
					<v-col cols="12" sm="12" md="3" class="align-center text-center text-md-left mt-4 mt-md-0">
						<Link id="logo" :href="route('dashboard')">
							<Logo variant="filled" />
						</Link>
					</v-col>
					<v-col cols="12" sm="12" md="4" class="my-4 my-sm-0">
						<global-search />
					</v-col>
					<v-col cols="12" sm="12" md="5" class="hidden-sm-and-down">
						<div class="d-flex align-center justify-center justify-md-end pl-2 pl-md-0">
							<div class="mr-5 welcome-header d-none d-md-inline-flex">
								<em>Welcome</em>,&nbsp;<strong>{{ auth.name }}</strong>
							</div>

							<edit-user :user="auth" profile @success="$inertia.reload({ only: ['auth'] })" />

							<Link :href="route('logout')"
								method="post"
								type="button"
								as="button"
								class="v-btn v-btn--text theme--dark v-size--default text-capitalize ml-0">
								<span class="v-btn__content">
									Logout
									<v-icon right>mdi-logout</v-icon>
								</span>
							</Link>
						</div>
					</v-col>
				</v-row>
			</v-container>
		</v-app-bar>

		<v-navigation-drawer
			v-model="drawer"
			absolute
			temporary>
			<app-mobile-menu @clicked="drawer = false" />
		</v-navigation-drawer>

		<v-main>
			<nav v-if="$vuetify.breakpoint.mdAndUp" id="nav">
				<v-container class="py-0" fluid>
					<v-toolbar elevation="0" height="42" color="transparent">
						<v-toolbar-items class="hidden-sm-and-down w-100">
							<Link class="v-btn v-btn--has-bg theme--light elevation-0 v-size--default px-8 transparent"
								:class="{'active': route().current('dashboard')}"
								:href="route('dashboard')">
								<span class="v-btn__content">
									<v-icon left dark>mdi-view-dashboard</v-icon>
									Dashboard
								</span>
							</Link>
							<Link class="v-btn v-btn--has-bg theme--light elevation-0 v-size--default px-8 transparent"
								:class="{'active': route().current('projects.*')}"
								:href="route('projects.index')">
								<span class="v-btn__content">
									<v-icon left dark>mdi-folder-check</v-icon>
									Projects
								</span>
							</Link>
							<template v-if="auth.user_type === 1">
								<Link class="v-btn v-btn--has-bg theme--light elevation-0 v-size--default px-8 transparent"
									:class="{'active': route().current('stats')}"
									:href="route('stats.reports')">
									<span class="v-btn__content">
										<v-icon left dark>mdi-chart-box</v-icon>
										Stats
									</span>
								</Link>
								<v-menu
									offset-y
									open-on-hover
									open-delay="200"
									content-class="elevation-0 main-menu-dropdown rounded-0">
									<template #activator="{ on }">
										<v-btn elevation="0"
											color="transparent"
											class="px-8"
											:class="{'active': route().current('invoices.*')}"
											v-on="on">
											<v-icon left dark>mdi-bank-check</v-icon>
											Invoices
											<v-icon right>mdi-chevron-down</v-icon>
										</v-btn>
									</template>
									<v-list
										class="pr-0 pl-0"
										dense
										outlined
										flat>
										<v-list-item dense class="px-0">
											<Link class="v-btn v-btn--has-bg theme--dark elevation-0 v-size--small px-8 text-capitalize transparent v-btn--block justify-start"
												:href="route('invoices.index')">
												<span class="v-btn__content text-left">All Invoices</span>
											</Link>
										</v-list-item>
										<v-list-item dense class="px-0">
											<Link class="v-btn v-btn--has-bg theme--dark elevation-0 v-size--small px-8 text-capitalize transparent v-btn--block justify-start"
												:href="route('invoices.index', { status: 'outstanding' })">
												<span class="v-btn__content">Outstanding Invoices</span>
											</Link>
										</v-list-item>
									</v-list>
								</v-menu>
								<Link class="v-btn v-btn--has-bg theme--light elevation-0 v-size--default px-8 transparent"
									:class="{'active': route().current('clients.*')}"
									:href="route('clients.index')">
									<span class="v-btn__content">
										<v-icon left dark>mdi-account-circle-outline</v-icon>
										Clients
									</span>
								</Link>
								<Link class="v-btn v-btn--has-bg theme--light elevation-0 v-size--default px-8 transparent ml-auto"
									:class="{'active': route().current('users.*')}"
									:href="route('users.index')">
									<span class="v-btn__content">
										<v-icon left dark>mdi-account-group</v-icon>
										Users
									</span>
								</Link>
								<Link class="v-btn v-btn--has-bg theme--light elevation-0 v-size--default px-8 transparent"
									:class="{'active': route().current('settings')}"
									:href="route('settings')">
									<span class="v-btn__content">
										<v-icon left dark>mdi-cog</v-icon>
										Settings
									</span>
								</Link>
							</template>
						</v-toolbar-items>
					</v-toolbar>
				</v-container>
			</nav>
			<nav v-else id="nav">
				<v-container class="py-0" fluid>
					<v-toolbar elevation="0" height="42" color="transparent">
						<v-toolbar-items class="w-100 text-center justify-center">
							<v-btn elevation="0" color="transparent" block @click.stop="drawer = !drawer">
								<v-icon>mdi-menu</v-icon>
								<span class="pl-2">Main Menu</span>
							</v-btn>
						</v-toolbar-items>
					</v-toolbar>
				</v-container>
			</nav>

			<div id="page-container">
				<slot />
			</div>
		</v-main>

		<v-footer padless dark app absolute>
			<v-col
				class="text-center"
				cols="12">
				<Copyright light />
			</v-col>
		</v-footer>

		<v-snackbar
			v-model="snack"
			:timeout="snackTimeout"
			top
			:color="snackColor">
			{{ snackText }}

			<template #action="{ attrs }">
				<v-btn
					v-bind="attrs"
					text
					@click="snack = false">
					Close
				</v-btn>
			</template>
		</v-snackbar>
	</v-app>
</template>
<script>
import Copyright from '@/Shared/Copyright'
import EditUser from '@/Pages/Backend/Users/Edit'
import AppMobileMenu from '@/Shared/AppMobileMenu.vue'
import GlobalSearch from '@/Pages/Backend/Components/GlobalSearch.vue'

export default {
	components: { GlobalSearch, AppMobileMenu, EditUser, Copyright },
	props: {
		flash: {
			type: Object,
			default: () => {
				return {
					'message': '',
					'error': ''
				}
			}
		},
		auth: {
			type: Object,
			default: () => {}
		},
		settings: {
			type: Object,
			default: () => {}
		}
	},
	data () {
		return {
			drawer: null,
			snack: null,
			snackTimeout: 6000,
			snackColor: '',
			snackText: ''
		}
	},
	computed: {
		//
	},
	watch: {
		flash () {
			if (this.flash.message) {
				this.snack = true
				this.snackColor = 'success'
				this.snackTimeout = 6000
				this.snackText = this.flash.message
			}

			if (this.flash.error) {
				this.snack = true
				this.snackColor = 'error'
				this.snackTimeout = 8000
				this.snackText = this.flash.error
			}
		}
	}
}
</script>
